import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Eyebrow } from '@/components/generals/eyebrow'
import { featureStories, specCards } from '@/constants/estate'

/*
   The residences
   Section introduction, then the property spec cards in a responsive grid
   (four columns on desktop, two on tablet, one on mobile), followed by three
   alternating image and text stories about the key features.
*/
export const ResidencesSection: FC = (): ReactNode => {
    return (
        <section id='residences' aria-labelledby='residences-heading' className='bg-sand py-24 lg:py-32'>
            <div className='mx-auto max-w-7xl px-6 lg:px-10'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <Reveal className='lg:col-span-7'>
                        <Eyebrow>
                            The residences
                        </Eyebrow>

                        <h2 id='residences-heading' className='mt-6 max-w-2xl font-heading text-4xl leading-[1.08] sm:text-5xl'>
                            Three bedrooms, all on <em className='italic text-forest'>one level</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={120} className='lg:col-span-5'>
                        <p className='text-[1.0625rem] leading-relaxed text-muted-foreground'>
                            Each of Rockwood's fourteen simplexes is freestanding and single-storey, designed around the way people really live: open-plan living that flows towards a level garden, a fireplace for winter, and a double garage for the cars and everything else.
                        </p>
                    </Reveal>
                </div>

                {/* Property spec cards */}
                <ul className='mt-14 grid gap-4 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4'>
                    {specCards.map((card, index) => {
                        const Icon = card.icon

                        return (
                            <li key={card.title}>
                                <Reveal delay={(index % 4) * 80} className='h-full'>
                                    <article className='group h-full rounded-2xl border border-stone bg-cream p-7 transition-[transform,border-color,box-shadow] duration-500 ease-out hover:-translate-y-1 hover:border-forest/30 hover:shadow-[0_24px_48px_-30px_rgba(47,79,79,0.45)]'>
                                        <span className='inline-flex size-12 items-center justify-center rounded-full border border-forest/20 text-forest transition-colors duration-500 group-hover:border-forest group-hover:bg-forest group-hover:text-cream'>
                                            <Icon aria-hidden='true' className='size-5' />
                                        </span>

                                        <h3 className='mt-6 font-heading text-xl'>
                                            {card.title}
                                        </h3>

                                        <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                                            {card.description}
                                        </p>
                                    </article>
                                </Reveal>
                            </li>
                        )
                    })}
                </ul>

                {/* Feature stories, alternating sides on desktop */}
                <div className='mt-24 space-y-24 lg:mt-32 lg:space-y-32'>
                    {featureStories.map((story, index) => (
                        <article key={story.id} aria-labelledby={`${story.id}-heading`} className='grid items-center gap-10 lg:grid-cols-12 lg:gap-16'>
                            <Reveal className={cn('lg:col-span-7', index % 2 === 1 && 'lg:order-2')}>
                                <div className='group relative aspect-[4/3] overflow-hidden rounded-3xl bg-stone'>
                                    <Image
                                        src={story.image}
                                        alt={story.imageAlt}
                                        fill
                                        sizes='(min-width: 1280px) 700px, (min-width: 1024px) 58vw, 100vw'
                                        className='object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]'
                                    />
                                </div>
                            </Reveal>

                            <Reveal delay={120} className={cn('lg:col-span-5', index % 2 === 1 && 'lg:order-1')}>
                                <p aria-hidden='true' className='font-heading text-2xl italic text-brass'>
                                    {`0${index + 1}`}
                                </p>

                                <Eyebrow className='mt-4'>
                                    {story.eyebrow}
                                </Eyebrow>

                                <h3 id={`${story.id}-heading`} className='mt-5 font-heading text-3xl leading-tight sm:text-4xl'>
                                    {story.title}
                                </h3>

                                <p className='mt-5 leading-relaxed text-muted-foreground'>
                                    {story.body}
                                </p>

                                <ul className='mt-8 space-y-3 border-t border-stone pt-6'>
                                    {story.points.map(point => (
                                        <li key={point} className='flex gap-3 text-sm text-charcoal/85'>
                                            <Check aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-forest' />
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                            </Reveal>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    )
}
